/* eslint-disable no-debugger */
import * as Yup from 'yup';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack5';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import { Box, Card, Grid, Stack, TextField, Autocomplete, Typography } from '@material-ui/core';
import CaseConvertor from '../../common-components/CaseConvertor';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import {
  createScoringModel,
  updateScoringModel,
  resetCreateScoringStatus,
  resetScoringError
} from '../../../redux/slices/scoring';
// routes
import Label from '../../Label';

CreateScoringForm.propTypes = {
  editId: PropTypes.number,
  setVisibleComponent: PropTypes.func,
  currentScoringModel: PropTypes.object
};

export default function CreateScoringForm({ editId, externalModelApi, setVisibleComponent, currentScoringModel = {} }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { error, createScoringStatus, isLoading } = useSelector((state) => state.scoring);

  useEffect(() => {
    if (error || createScoringStatus) {
      enqueueSnackbar(
        (createScoringStatus && (!editId ? 'New Client Created Successfully' : 'Client Updated Successfully')) ||
          (error && error),
        { variant: error ? 'error' : 'success' }
      );
      dispatch(resetScoringError());
    }
    if (createScoringStatus) {
      setVisibleComponent('List');
      formik.resetForm();
      dispatch(resetCreateScoringStatus());
    }
  }, [error, createScoringStatus]);

  const NewClientSchema = Yup.object().shape({
    modelName: Yup.string().required('Client Name is required'),
    externalModel: Yup.mixed().required('External Model is required')
  });

  const editTempData = {
    editModelName: currentScoringModel.scoring_model_name ? currentScoringModel.scoring_model_name : '',
    editExternalModel: currentScoringModel.external_model ? currentScoringModel.external_model : '',
    editRequiredColumns: currentScoringModel.required_columns ? currentScoringModel.required_columns : '',
    editColumnNames: currentScoringModel.external_model ? currentScoringModel.external_model.columns : []
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      modelName: editId ? editTempData.editModelName : '',
      externalModel: editId ? editTempData.editExternalModel : '',
      requiredColumns: editId ? editTempData.editRequiredColumns : '',
      columnNames: editId ? editTempData.editColumnNames : []
    },
    validationSchema: NewClientSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const modelData = {
          scoring_model_name: CaseConvertor(values.modelName),
          external_model: values.externalModel.id
        };
        dispatch(!editId ? createScoringModel(modelData) : updateScoringModel(modelData, editId));
        setSubmitting(false);
      } catch (error) {
        console.error(error);
        setErrors(error);
      }
    }
  });

  const { errors, values, touched, handleSubmit, isSubmitting, setFieldValue, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="Model Name"
                    required
                    {...getFieldProps('modelName')}
                    value={values.modelName}
                    error={Boolean(touched.modelName && errors.modelName)}
                    helperText={touched.modelName && errors.modelName}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <Autocomplete
                    fullWidth
                    value={values?.externalModel ? values?.externalModel : null}
                    name="externalModel"
                    options={externalModelApi}
                    onChange={(e, value) => {
                      setFieldValue('externalModel', value);
                      setFieldValue('requiredColumns', value?.required_columns);
                      setFieldValue('columnNames', value?.columns);
                    }}
                    getOptionLabel={(option) => (option ? option.external_model_name : '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.externalModel && errors.externalModel)}
                        helperText={touched.externalModel && errors.externalModel}
                        required
                        {...params}
                        label="External Model"
                        margin="none"
                      />
                    )}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField fullWidth label="Required Columns" value={values.requiredColumns} disabled />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }} alignItems="center">
                  <Typography variant="subtitle1">Columns : </Typography>
                  {values?.columnNames?.map((a, i) => (
                    <Label key={i} variant="outlined" color="primary">
                      {a}
                    </Label>
                  ))}
                </Stack>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton type="submit" variant="contained" loading={isLoading}>
                    {!editId ? 'Save' : 'Save Changes'}
                  </LoadingButton>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
